import React, { FC, useCallback, useMemo } from 'react';

import { EvSection, EvStackedBarChart } from '@evinced-private/ui-common';

import { createStrategicTickPositions } from '../../../../common/helpers/chartHelpers';
import { ERROR_TYPE } from '../../types/chartErrors';
import { ErrorView } from '../error-view/ErrorView';
import { ChartSkeleton } from '../skeletons/chart-skeleton/ChartSkeleton';

import { prepareDataForCriticalIssuesStackedChart } from './helpers/dataTransformHelpers';
import { createTooltipContent } from './helpers/tooltipHelpers';
import { CHART_CONSTANTS, CHART_TEXT } from './constants';
import { AccessibilityCriticalIssuesStackedChartProps } from './types';

import './AccessibilityCriticalIssuesStackedChart.scss';

export const AccessibilityCriticalIssuesStackedChart: FC<
	AccessibilityCriticalIssuesStackedChartProps
> = ({
	data,
	isLoading = false,
	error = null,
	title = CHART_TEXT.TITLE,
	height = CHART_CONSTANTS.HEIGHT,
	className = '',
	showTable = true
}) => {
	const { chartData, patterns, categories } = useMemo(() => {
		if (!data?.length) {
			return { chartData: [], patterns: [], categories: [] };
		}

		const result = prepareDataForCriticalIssuesStackedChart(data);
		const categories = result.chartData.map((item) => item.date);
		return { ...result, categories };
	}, [data]);

	const hasError = !!(error || !data || data.length === 0);
	const getErrorType = (): ERROR_TYPE => (error ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA);

	// EvStackedBarChart can't handle string values, so we use a custom tooltip for the number formatting
	const customTooltip = useCallback(
		(context) => {
			const { y, name, point } = context;
			// Extract the date from the chartData at the corresponding index
			const dataPoint = chartData[point?.index || 0];
			const dateStr = dataPoint?.date || '';
			const tooltipContent = createTooltipContent(name, dateStr, y);

			return `<div class="chart-tooltip">
				${tooltipContent}
			</div>`;
		},
		[chartData]
	);

	const gridYValuesTickInterval = useMemo(() => {
		if (!chartData?.length) {
			return undefined;
		}

		const maxTotal = Math.max(...chartData.map((item) => item.total || 0));

		if (maxTotal < CHART_CONSTANTS.WHOLE_NUMBER_THRESHOLD) {
			return 1;
		}

		return undefined;
	}, [chartData]);

	const customYaxisLabels = useCallback((value: number) => {
		if (value >= 1000) {
			return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`;
		}
		return value.toString();
	}, []);

	const chartClassName = `accessibility-critical-issues-stacked-chart ${className}`.trim();

	if (isLoading) {
		return (
			<EvSection className={chartClassName}>
				<div className="accessibility-critical-issues-stacked-chart-wrapper">
					<ChartSkeleton />
				</div>
			</EvSection>
		);
	}

	return (
		<EvSection className={chartClassName}>
			<div className="accessibility-critical-issues-stacked-chart-wrapper">
				<EvStackedBarChart
					title={title}
					data={chartData}
					patterns={patterns}
					height={height}
					customTooltip={customTooltip}
					customYaxisLabels={customYaxisLabels}
					gridYValuesTickInterval={gridYValuesTickInterval}
					className="accessibility-critical-issues-stacked-chart-inner"
					withLegend={true}
					withFiltering={true}
					excludeFields={CHART_CONSTANTS.EXCLUDED_FIELDS}
					headingLevel={CHART_CONSTANTS.HEADING_LEVEL}
					yAxisTitle={CHART_CONSTANTS.Y_AXIS_TITLE}
					isError={hasError}
					renderErrorState={() => <ErrorView errorType={getErrorType()} />}
					xAxis={{
						type: 'category' as const,
						categories,
						tickPositions: createStrategicTickPositions(categories.length)
					}}
					{...(showTable && {
						tableProps: {
							dataType: 'Critical Issues Stacked Data',
							columnsPattern: [
								{ name: CHART_TEXT.TABLE_COLUMNS.DATE, field: 'date', sort: true },
								{ name: CHART_TEXT.TABLE_COLUMNS.CRITICAL, field: 'critical', sort: true },
								{ name: CHART_TEXT.TABLE_COLUMNS.SERIOUS, field: 'serious', sort: true },
								{ name: CHART_TEXT.TABLE_COLUMNS.TOTAL, field: 'total', sort: true }
							],
							defaultSortByField: 'date'
						}
					})}
				/>
			</div>
		</EvSection>
	);
};
